/**
 * Página dedicada de detalhe de 1 grupo da esteira.
 *
 * Substitui o EsteiraGroupDetailDialog (modal) por uma rota
 * `/meta/esteira/:groupId`. Layout:
 *   - Header: voltar + nome + status + ações (editar/pausar/resume/atualizar)
 *   - Stats row (6 cards)
 *   - Detalhes do grupo (config compacta, collapsible)
 *   - Tabs: Próximos envios | Histórico
 *
 * "Próximos envios" lista chatmeta_esteira_broadcasts (status=pending) com
 * preview Messenger-like.
 * "Histórico" tabela completa com page_name + thumbnail do template.
 */

import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    ArrowLeft, Loader2, RefreshCw, ChevronDown, ChevronUp,
    Sparkles, History, Edit3, Pause, Play, Pencil, FolderSync,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
    getEsteiraGroup, getEsteiraGroupStats,
    listEsteiraBroadcasts, listEsteiraUpcomingSlots, replenishEsteiraGroupNow,
    pauseEsteiraGroup, resumeEsteiraGroup,
    getEsteiraFormOptions,
} from '@/services/api';
import type { UpcomingSlot } from '@/services/api/esteira';
import type { EsteiraBroadcastStatus, EsteiraStatus } from '@/types/esteira';
import EsteiraGroupFormDialog from './components/EsteiraGroupFormDialog';
import EsteiraBroadcastEditDialog from './components/EsteiraBroadcastEditDialog';
import ManageEsteiraPagesDialog from './components/ManageEsteiraPagesDialog';

type Tab = 'upcoming' | 'history';

/** Label exibível pra uma page: prefere page_name (humano), depois fb_page_id
 *  (id do Facebook — útil pra debug), por último o id interno (que o user
 *  não reconhece, mas é melhor que nada). */
function formatPageLabel(p: {
    page_name?: string | null;
    fb_page_id?: string | null;
    page_id: number;
}, fallbackName?: string | null): string {
    if (p.page_name) return p.page_name;
    if (fallbackName) return fallbackName;
    if (p.fb_page_id) return `FB: ${p.fb_page_id}`;
    return `Página #${p.page_id}`;
}

const STATUS_COLORS: Record<EsteiraStatus, string> = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    paused: 'bg-amber-50 text-amber-700 border-amber-200',
    archived: 'bg-gray-50 text-gray-500 border-gray-200',
};

const STATUS_LABELS: Record<EsteiraStatus, string> = {
    active: 'Ativo',
    paused: 'Pausado',
    archived: 'Arquivado',
};

const BROADCAST_STATUS_COLORS: Record<EsteiraBroadcastStatus, string> = {
    pending: 'bg-blue-50 text-blue-700 border-blue-200',
    sending: 'bg-amber-50 text-amber-700 border-amber-200',
    sent: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    failed: 'bg-red-50 text-red-700 border-red-200',
    skipped: 'bg-gray-50 text-gray-500 border-gray-200',
    cancelled: 'bg-gray-50 text-gray-400 border-gray-200',
};

const fmtDate = (iso?: string | null) => {
    if (!iso) return '–';
    try {
        return new Date(iso).toLocaleString('pt-BR', {
            day: '2-digit', month: '2-digit', year: '2-digit',
            hour: '2-digit', minute: '2-digit',
        });
    } catch { return iso; }
};

export default function EsteiraGroupPage() {
    const { groupId: groupIdParam } = useParams<{ groupId: string }>();
    const groupId = Number(groupIdParam);
    const navigate = useNavigate();
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [tab, setTab] = useState<Tab>('upcoming');
    const [historyStatus, setHistoryStatus] = useState<EsteiraBroadcastStatus | 'all'>('all');
    const [editOpen, setEditOpen] = useState(false);
    const [managePagesOpen, setManagePagesOpen] = useState(false);
    const [configExpanded, setConfigExpanded] = useState(false);
    // Filtro de visualização compartilhado entre as 2 abas (Próximos + Histórico).
    // Padrão: mostra 'active' (envio + fallback aprovado), esconde 'retry'.
    const [showActive, setShowActive] = useState(true);
    const [showRetry, setShowRetry] = useState(false);

    const { data: group, isLoading: groupLoading } = useQuery({
        queryKey: ['esteira-group', groupId],
        queryFn: () => getEsteiraGroup(groupId),
        enabled: !!groupId,
    });

    const { data: stats } = useQuery({
        queryKey: ['esteira-group-stats', groupId],
        queryFn: () => getEsteiraGroupStats(groupId),
        enabled: !!groupId,
        refetchInterval: 1000 * 15,
    });

    // Filtro de tipo de envio vai pro SERVIDOR (não dá pra filtrar no cliente:
    // o limit/paginação é do servidor, então esconder itens depois de buscar 50
    // mostraria menos de 50 e o "mostrando N"/total mentiriam).
    // Ambos marcados = sem filtro (todos). Nenhum = não consulta (lista vazia).
    const leadFilterParam = showActive && showRetry
        ? undefined
        : showActive
            ? 'active'
            : showRetry
                ? 'error_retry'
                : undefined;
    const anyLeadFilter = showActive || showRetry;

    const { data: upcoming, isLoading: upcomingLoading } = useQuery({
        queryKey: ['esteira-upcoming', groupId, leadFilterParam],
        queryFn: () => listEsteiraUpcomingSlots(groupId, 50, leadFilterParam),
        enabled: !!groupId && tab === 'upcoming' && anyLeadFilter,
        staleTime: 1000 * 30,
        refetchInterval: 1000 * 30,
    });

    const { data: history, isLoading: historyLoading } = useQuery({
        queryKey: ['esteira-history', groupId, historyStatus, leadFilterParam],
        queryFn: () => listEsteiraBroadcasts(groupId, {
            status: historyStatus === 'all' ? undefined : historyStatus,
            lead_filter: leadFilterParam,
            per_page: 50,
        }),
        enabled: !!groupId && tab === 'history' && anyLeadFilter,
        refetchInterval: 1000 * 15,
    });

    const { data: formOptions } = useQuery({
        queryKey: ['esteira-form-options'],
        queryFn: getEsteiraFormOptions,
        enabled: !!groupId,
        staleTime: 1000 * 60 * 5,
    });
    const pageNameById = new Map<number, string>(
        (formOptions?.pages ?? []).map((p) => [p.id, p.page_name || `Página #${p.id}`]),
    );

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ['esteira-group', groupId] });
        queryClient.invalidateQueries({ queryKey: ['esteira-group-stats', groupId] });
        queryClient.invalidateQueries({ queryKey: ['esteira-upcoming', groupId] });
        queryClient.invalidateQueries({ queryKey: ['esteira-history', groupId] });
        queryClient.invalidateQueries({ queryKey: ['esteira-groups'] });
    };

    const pauseMutation = useMutation({
        mutationFn: () => pauseEsteiraGroup(groupId),
        onSuccess: () => { toast({ title: 'Grupo pausado' }); invalidate(); },
    });
    const resumeMutation = useMutation({
        mutationFn: () => resumeEsteiraGroup(groupId),
        onSuccess: () => { toast({ title: 'Grupo ativado' }); invalidate(); },
    });
    const replenishMutation = useMutation({
        mutationFn: () => replenishEsteiraGroupNow(groupId),
        onSuccess: (r) => {
            const deleted = r.deleted_pending ?? 0;
            const inserted = r.inserted ?? 0;
            const parts: string[] = [];
            if (deleted > 0) parts.push(`${deleted} pendentes removidos`);
            if (inserted > 0) {
                parts.push(`${inserted} novos ${inserted === 1 ? 'envio agendado' : 'envios agendados'}`);
            }
            toast({
                title: 'Fila regenerada',
                description: parts.length > 0
                    ? parts.join(' • ') + '.'
                    : 'Nada a atualizar.',
            });
            invalidate();
        },
        onError: (e: any) => {
            toast({
                title: 'Falha ao atualizar fila',
                description: e?.response?.data?.detail || e?.message || 'erro desconhecido',
                variant: 'destructive',
            });
        },
    });

    if (!groupId || isNaN(groupId)) {
        return <Navigate to="/meta/esteira" replace />;
    }

    if (groupLoading || !group) {
        return (
            <div className="flex items-center justify-center py-32">
                <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
            </div>
        );
    }

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/meta/esteira')}
                        className="shrink-0 -ml-2"
                    >
                        <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
                    </Button>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="text-2xl font-bold tracking-tight truncate">
                                {group.name}
                            </h1>
                            <Badge variant="outline" className={STATUS_COLORS[group.status]}>
                                {STATUS_LABELS[group.status]}
                            </Badge>
                            <Badge variant="outline" className="font-mono">
                                {group.target_country}
                            </Badge>
                        </div>
                        {group.description && (
                            <p className="text-sm text-gray-500 mt-1">{group.description}</p>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => replenishMutation.mutate()}
                        disabled={replenishMutation.isPending || group.status !== 'active'}
                    >
                        {replenishMutation.isPending
                            ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                            : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                        Atualizar fila
                    </Button>
                    {group.status === 'active' && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => pauseMutation.mutate()}
                            disabled={pauseMutation.isPending}
                        >
                            <Pause className="w-3.5 h-3.5 mr-1.5" /> Pausar
                        </Button>
                    )}
                    {group.status === 'paused' && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => resumeMutation.mutate()}
                            disabled={resumeMutation.isPending}
                        >
                            <Play className="w-3.5 h-3.5 mr-1.5" /> Retomar
                        </Button>
                    )}
                    {group.status !== 'archived' && (
                        <>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setManagePagesOpen(true)}
                            >
                                <FolderSync className="w-3.5 h-3.5 mr-1.5" /> Páginas
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setEditOpen(true)}
                            >
                                <Edit3 className="w-3.5 h-3.5 mr-1.5" /> Editar
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <StatCard label="Total na fila" value={stats?.total_broadcasts ?? '–'} color="gray" />
                <StatCard label="Pendentes" value={stats?.total_pending ?? '–'} color="blue" />
                <StatCard label="Enviados" value={stats?.total_sent ?? '–'} color="emerald" />
                <StatCard label="Falhas" value={stats?.total_failed ?? '–'} color="red" />
                <StatCard label="Leads alcançados" value={stats?.total_leads_reached ?? '–'} color="violet" />
                <StatCard
                    label="Próximo envio"
                    value={fmtDate(stats?.next_scheduled_at)}
                    color="gray"
                    small
                />
            </div>

            {/* Detalhes do grupo (collapsible) */}
            <div className="border rounded-lg bg-white">
                <button
                    type="button"
                    onClick={() => setConfigExpanded((v) => !v)}
                    className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors"
                >
                    <span className="text-sm font-medium text-gray-700">Detalhes do grupo</span>
                    {configExpanded
                        ? <ChevronUp className="w-4 h-4 text-gray-400" />
                        : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </button>
                {configExpanded && (
                    <div className="border-t px-4 py-3 grid grid-cols-2 gap-3 text-sm bg-gray-50">
                        <InfoDomains label="Domínios fonte" domains={group.source_domains} />
                        <Info label="Tipo template" value={group.template_type_filter} />
                        <InfoPages
                            label="Páginas"
                            pageIds={group.page_ids}
                            pageNameById={pageNameById}
                            pagesMeta={group.pages_meta}
                        />
                        <InfoSchedule label="Horários" times={group.schedule_times} />
                        <Info label="Métrica" value={group.ranking_metric} />
                        <Info label="Período rank" value={`${group.ranking_period_days}d`} />
                        <Info label="Anti-repeat (template_id)" value={`últimos ${group.anti_repeat_window}`} />
                        <Info
                            label="Anti-repeat (copy)"
                            value={group.content_repeat_window > 0
                                ? `últimos ${group.content_repeat_window}`
                                : 'desligado'} />
                    </div>
                )}
            </div>

            {/* Tabs */}
            <div className="border-b flex gap-1">
                {[
                    { id: 'upcoming' as Tab, label: 'Próximos envios', icon: Sparkles },
                    { id: 'history' as Tab, label: 'Histórico', icon: History },
                ].map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => setTab(t.id)}
                        className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1.5 transition-colors ${
                            tab === t.id
                                ? 'border-violet-600 text-violet-700'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        <t.icon className="w-4 h-4" />
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Filtro de visualização — vale pras 2 abas (Próximos + Histórico) */}
            <div className="flex flex-wrap items-center gap-4 px-1 text-xs">
                <span className="text-gray-500">Mostrar:</span>
                <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                        type="checkbox"
                        className="h-3.5 w-3.5"
                        checked={showActive}
                        onChange={(e) => setShowActive(e.target.checked)}
                    />
                    Envio + template aprovado
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                        type="checkbox"
                        className="h-3.5 w-3.5"
                        checked={showRetry}
                        onChange={(e) => setShowRetry(e.target.checked)}
                    />
                    Retry de erros
                </label>
            </div>

            {/* Tab content */}
            {tab === 'upcoming' ? (
                !anyLeadFilter ? (
                    <EmptyState message="Marque ao menos um tipo em “Mostrar” para ver os envios" />
                ) : upcomingLoading ? (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
                    </div>
                ) : !upcoming || upcoming.items.length === 0 ? (
                    <EmptyState message="Sem envios pendentes na fila" />
                ) : (
                    <UpcomingGroupedTree
                        groupId={groupId}
                        slots={upcoming.items}
                        totalPending={upcoming.total_pending}
                        totalGroups={upcoming.total_groups}
                        pageNameById={pageNameById}
                    />
                )
            ) : (
                <div className="space-y-3">
                    <div className="flex items-center gap-2">
                        {(['all', 'pending', 'sent', 'failed'] as const).map((s) => (
                            <button
                                key={s}
                                type="button"
                                onClick={() => setHistoryStatus(s as any)}
                                className={`px-3 py-1 text-xs rounded-md border ${
                                    historyStatus === s
                                        ? 'bg-violet-600 text-white border-violet-600'
                                        : 'bg-white text-gray-600 border-gray-200'
                                }`}
                            >
                                {s === 'all' ? 'Todos' : s}
                            </button>
                        ))}
                    </div>
                    {!anyLeadFilter ? (
                        <EmptyState message="Marque ao menos um tipo em “Mostrar” para ver os envios" />
                    ) : historyLoading ? (
                        <div className="flex items-center justify-center py-16">
                            <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
                        </div>
                    ) : !history || history.items.length === 0 ? (
                        <EmptyState message="Nenhum broadcast ainda" />
                    ) : (
                        <>
                            <div className="text-xs text-gray-500 px-1">
                                <strong>{history.total}</strong> {history.total === 1 ? 'broadcast' : 'broadcasts'}
                                {history.items.length < history.total && (
                                    <span className="ml-1">(mostrando {history.items.length})</span>
                                )}
                            </div>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                {history.items.map((b) => (
                                    <HistoryCard
                                        key={b.id}
                                        broadcast={b}
                                        pageName={formatPageLabel(b, pageNameById.get(b.page_id))}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Edit dialog */}
            <EsteiraGroupFormDialog
                open={editOpen}
                onOpenChange={(o) => {
                    setEditOpen(o);
                    if (!o) invalidate();
                }}
                existingGroup={editOpen ? group : null}
            />

            {/* Manage pages dialog */}
            <ManageEsteiraPagesDialog
                open={managePagesOpen}
                onOpenChange={setManagePagesOpen}
                group={group}
                formOptions={formOptions}
            />
        </div>
    );
}

// ─── Components ─────────────────────────────────────────────────────

function StatCard({
    label, value, color, small,
}: { label: string; value: any; color: string; small?: boolean }) {
    const colorClass = {
        gray: 'text-gray-900',
        blue: 'text-blue-700 bg-blue-50/40',
        emerald: 'text-emerald-700 bg-emerald-50/40',
        red: 'text-red-700 bg-red-50/40',
        violet: 'text-violet-700 bg-violet-50/40',
    }[color] || 'text-gray-900';
    return (
        <div className={`rounded-lg p-3 border ${colorClass}`}>
            <div className="text-[10px] text-gray-500 uppercase font-medium tracking-wide">{label}</div>
            <div className={`${small ? 'text-sm' : 'text-xl'} font-bold mt-0.5`}>
                {typeof value === 'number' ? value.toLocaleString('pt-BR') : value}
            </div>
        </div>
    );
}

function Info({ label, value }: { label: string; value: any }) {
    return (
        <div>
            <div className="text-xs text-gray-500">{label}</div>
            <div className="text-sm text-gray-900 truncate">{value || '–'}</div>
        </div>
    );
}

function EmptyState({ message }: { message: string }) {
    return (
        <div className="text-center py-16 text-gray-400 text-sm border rounded-lg bg-white">
            {message}
        </div>
    );
}

// ─── Info enrich (popover lists) ────────────────────────────────────

function InfoDomains({ label, domains }: { label: string; domains: string[] }) {
    if (!domains?.length) return <Info label={label} value="–" />;
    const first2 = domains.slice(0, 2).join(', ');
    const rest = domains.length - 2;
    return (
        <div>
            <div className="text-xs text-gray-500">{label}</div>
            {domains.length <= 2 ? (
                <div className="text-sm text-gray-900 font-mono truncate">{first2}</div>
            ) : (
                <Popover>
                    <PopoverTrigger asChild>
                        <button className="text-sm text-gray-900 font-mono truncate text-left hover:underline flex items-center gap-1 max-w-full">
                            <span className="truncate">{first2}</span>
                            <span className="text-xs text-violet-600 shrink-0">+{rest}</span>
                            <ChevronDown className="w-3 h-3 shrink-0 opacity-60" />
                        </button>
                    </PopoverTrigger>
                    <PopoverContent className="p-2 w-[280px] max-h-[300px] overflow-y-auto">
                        <p className="text-[10px] uppercase font-medium text-gray-500 px-2 pb-1">
                            {domains.length} domínios
                        </p>
                        <div className="space-y-0.5">
                            {domains.map((d) => (
                                <div key={d} className="text-xs font-mono px-2 py-1 rounded hover:bg-gray-50">
                                    {d}
                                </div>
                            ))}
                        </div>
                    </PopoverContent>
                </Popover>
            )}
        </div>
    );
}

function InfoPages({
    label, pageIds, pageNameById, pagesMeta,
}: {
    label: string;
    pageIds: number[];
    pageNameById: Map<number, string>;
    /** Enriquecido pelo backend. Inclui pages master mesmo se inativas.
     *  Page ausente aqui (mas presente em pageIds) = foi excluída. */
    pagesMeta?: Array<{ id: number; page_name?: string | null; fb_page_id?: string | null }> | null;
}) {
    if (!pageIds?.length) return <Info label={label} value="–" />;
    // Map id → label preferindo pages_meta do backend (sobrevive a is_active=0).
    // Fallback pra pageNameById (form-options, só ativas). Último: "Página #N excluída"
    // (porque se nem o backend tem, foi DELETADA de chatmeta_pages).
    const metaById = new Map<number, { page_name?: string | null; fb_page_id?: string | null }>(
        (pagesMeta ?? []).map((m) => [m.id, m]),
    );
    const names = pageIds.map((id) => {
        const m = metaById.get(id);
        if (m?.page_name) return m.page_name;
        const fromOptions = pageNameById.get(id);
        if (fromOptions) return fromOptions;
        if (m?.fb_page_id) return `FB: ${m.fb_page_id}`;
        return `Página #${id} (excluída)`;
    });
    const first2 = names.slice(0, 2).join(', ');
    const rest = names.length - 2;
    return (
        <div>
            <div className="text-xs text-gray-500">{label}</div>
            {names.length <= 2 ? (
                <div className="text-sm text-gray-900 truncate">{first2}</div>
            ) : (
                <Popover>
                    <PopoverTrigger asChild>
                        <button className="text-sm text-gray-900 truncate text-left hover:underline flex items-center gap-1 max-w-full">
                            <span className="truncate">{first2}</span>
                            <span className="text-xs text-violet-600 shrink-0">+{rest}</span>
                            <ChevronDown className="w-3 h-3 shrink-0 opacity-60" />
                        </button>
                    </PopoverTrigger>
                    <PopoverContent className="p-2 w-[280px] max-h-[320px] overflow-y-auto">
                        <p className="text-[10px] uppercase font-medium text-gray-500 px-2 pb-1">
                            {names.length} páginas
                        </p>
                        <div className="space-y-0.5">
                            {names.map((n, i) => (
                                <div key={i} className="text-xs px-2 py-1 rounded hover:bg-gray-50 truncate">
                                    {n}
                                </div>
                            ))}
                        </div>
                    </PopoverContent>
                </Popover>
            )}
        </div>
    );
}

function InfoSchedule({ label, times }: { label: string; times: string[] }) {
    if (!times?.length) return <Info label={label} value="–" />;
    return (
        <div>
            <div className="text-xs text-gray-500 mb-1">{label}</div>
            <div className="flex flex-wrap gap-1">
                {times.map((t) => (
                    <Badge key={t} variant="outline" className="text-[10px] font-mono px-1.5 py-0">
                        {t}
                    </Badge>
                ))}
            </div>
        </div>
    );
}

// ─── UpcomingGroupedTree: árvore horário → template → páginas ───────
//
// Substitui o grid raso de cards individuais. Cada nível:
//   1. Horário (scheduled_at, ASC)
//   2. Template (template_intel_id) — preview Messenger + lista de páginas
//   3. Páginas: primeiras 3 inline + "+N páginas" abre modal
//
// Em esteiras synchronized (1 template por horário, várias páginas), evita
// repetir o mesmo card N vezes. Em per_page_random, cada template vira sua
// própria linha sob o mesmo horário.

function UpcomingGroupedTree({
    groupId, slots, totalPending, totalGroups, pageNameById,
}: {
    groupId: number;
    slots: UpcomingSlot[];
    totalPending: number;
    totalGroups: number;
    pageNameById: Map<number, string>;
}) {
    // O filtro de tipo de envio é aplicado no SERVIDOR (lead_filter na query) —
    // aqui os slots já chegam filtrados, senão o limit devolveria menos itens.

    // Agrupa por scheduled_at — cada slot já vem agregado por template
    // (1 slot = 1 horário+template, com page_count + pages[] + payload sample).
    // Synchronized: 1 slot por horário. per_page_random: N slots por horário.
    const byHorario = new Map<string, UpcomingSlot[]>();
    for (const s of slots) {
        if (!byHorario.has(s.scheduled_at)) byHorario.set(s.scheduled_at, []);
        byHorario.get(s.scheduled_at)!.push(s);
    }

    return (
        <div className="space-y-4">
            <div className="text-xs text-gray-500 px-1">
                <strong>{totalPending}</strong> {totalPending === 1 ? 'envio pendente' : 'envios pendentes'}
                {' · '}
                <strong>{totalGroups}</strong> {totalGroups === 1 ? 'horário' : 'horários'}
                {slots.length < totalGroups && (
                    <span className="ml-1">(mostrando os próximos {slots.length})</span>
                )}
            </div>

            <div className="space-y-5">
                {Array.from(byHorario.entries()).map(([horario, slotsAtTime]) => {
                    const totalEnviosHorario = slotsAtTime.reduce((s, x) => s + x.page_count, 0);
                    return (
                        <div key={horario} className="space-y-2">
                            {/* Cabeçalho do horário */}
                            <div className="flex items-baseline gap-2 border-b pb-1">
                                <span className="text-sm font-mono font-semibold text-violet-700">
                                    {fmtDate(horario)}
                                </span>
                                <span className="text-[11px] text-gray-500">
                                    {totalEnviosHorario} {totalEnviosHorario === 1 ? 'envio' : 'envios'}
                                    {slotsAtTime.length > 1 && ` · ${slotsAtTime.length} templates`}
                                </span>
                            </div>

                            {/* Templates do horário */}
                            <div className="space-y-2 pl-3 border-l-2 border-violet-100">
                                {slotsAtTime.map((slot) => (
                                    <UpcomingTemplateRow
                                        key={slot.template_intel_id}
                                        groupId={groupId}
                                        slot={slot}
                                        pageNameById={pageNameById}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function UpcomingTemplateRow({
    groupId, slot, pageNameById,
}: {
    groupId: number;
    slot: UpcomingSlot;
    pageNameById: Map<number, string>;
}) {
    const [pagesModalOpen, setPagesModalOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);

    const tplId = slot.template_intel_id;
    const horario = slot.scheduled_at;

    // O slot já vem com o sample de payload — usa direto pro preview.
    const { title, subtitle, imageUrl, text, buttons } = extractPreview(slot);
    // Preview do template de fallback pré-selecionado (se o grupo tem fallback).
    const fbPreview = slot.fallback_message_payload
        ? extractPreview({ message_payload: slot.fallback_message_payload })
        : null;

    const pageNames = slot.pages
        .map((p) => formatPageLabel(p, pageNameById.get(p.page_id)))
        .sort((a, b) => a.localeCompare(b, 'pt-BR'));

    // O EditDialog espera 1 entrada por broadcast (id + page_id + payload).
    // Sintetiza do slot agregado — todos compartilham o mesmo payload.
    const editDialogItems = useMemo(
        () => slot.pages.map((p) => ({
            id: p.broadcast_id,
            page_id: p.page_id,
            page_name: p.page_name,
            fb_page_id: p.fb_page_id,
            scheduled_at: slot.scheduled_at,
            template_intel_id: slot.template_intel_id,
            message_payload: slot.message_payload,
            applied_buttons: slot.applied_buttons,
        })),
        [slot],
    );
    // Mostra a 1ª página inline; o resto (qualquer >1) vira pill "+N páginas"
    // que abre modal. Threshold baixo deixa óbvio que existem mais quando
    // o template é compartilhado entre N páginas.
    const INLINE_PAGES = 1;
    const inlinePages = pageNames.slice(0, INLINE_PAGES);
    const restCount = pageNames.length - inlinePages.length;

    const isErrorRetry = slot.lead_filter === 'error_retry';

    return (
        <>
            <div
                className={`border rounded-lg p-3 hover:border-violet-300 transition-colors flex gap-3 ${
                    isErrorRetry
                        ? 'bg-amber-50/40 border-amber-200'
                        : 'bg-white'
                }`}
            >
                {/* Lateral: tpl badge + lista de páginas */}
                <div className="flex flex-col items-start gap-2 min-w-[180px] max-w-[200px] shrink-0">
                    <Badge variant="outline" className="text-[10px]">
                        tpl #{tplId}
                    </Badge>
                    {isErrorRetry && (
                        <Badge
                            variant="outline"
                            className="text-[10px] bg-amber-100 text-amber-800 border-amber-300 font-medium"
                            title="Disparo extra depois do horário normal, mirando leads que tiveram erro recuperável (ex: 551)"
                        >
                            ↻ Retry erros
                        </Badge>
                    )}
                    <div className="space-y-1 w-full">
                        <div className="text-[10px] uppercase font-medium text-gray-500 tracking-wide">
                            {pageNames.length} {pageNames.length === 1 ? 'página' : 'páginas'}
                        </div>
                        <div className="space-y-0.5">
                            {inlinePages.map((n) => (
                                <div
                                    key={n}
                                    className="text-[11px] text-gray-700 truncate"
                                    title={n}
                                >
                                    {n}
                                </div>
                            ))}
                            {restCount > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setPagesModalOpen(true)}
                                    className="text-[11px] text-violet-600 hover:underline font-medium"
                                >
                                    +{restCount} {restCount === 1 ? 'página' : 'páginas'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Preview Messenger-like — primário (raw) + fallback aprovado */}
                <div className="flex-1 min-w-0 space-y-2">
                    <PreviewBubble
                        title={title}
                        subtitle={subtitle}
                        imageUrl={imageUrl}
                        text={text}
                        buttons={buttons}
                    />
                    {fbPreview && (
                        <div className="rounded-lg border border-dashed border-violet-200 bg-violet-50/30 p-2">
                            <div className="text-[10px] font-medium text-violet-700 mb-1 flex flex-wrap items-center gap-1">
                                🛟 Fallback aprovado
                                {slot.fallback_template_name && (
                                    <span className="text-violet-400 font-normal">· {slot.fallback_template_name}</span>
                                )}
                                <span className="text-gray-400 font-normal">(enviado se o primário falhar fora da janela · rotaciona)</span>
                            </div>
                            <PreviewBubble
                                title={fbPreview.title}
                                subtitle={fbPreview.subtitle}
                                imageUrl={fbPreview.imageUrl}
                                text={fbPreview.text}
                                buttons={fbPreview.buttons}
                            />
                        </div>
                    )}
                </div>

                {/* Botão editar (lateral direita, ícone) */}
                <div className="shrink-0">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditOpen(true)}
                        title="Editar conteúdo do envio"
                        className="h-8 px-2 text-gray-500 hover:text-violet-700"
                    >
                        <Pencil className="w-3.5 h-3.5 mr-1" />
                        <span className="text-xs">Editar</span>
                    </Button>
                </div>
            </div>

            {/* Modal completo de páginas */}
            <Dialog open={pagesModalOpen} onOpenChange={setPagesModalOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Páginas que recebem este template</DialogTitle>
                        <DialogDescription>
                            tpl #{tplId} · {pageNames.length} {pageNames.length === 1 ? 'página' : 'páginas'} no mesmo horário
                        </DialogDescription>
                    </DialogHeader>
                    <div className="max-h-[60vh] overflow-y-auto -mx-6 px-6">
                        <ul className="space-y-1">
                            {pageNames.map((n) => (
                                <li
                                    key={n}
                                    className="text-sm text-gray-800 px-2 py-1.5 rounded hover:bg-gray-50 border border-transparent hover:border-gray-100 truncate"
                                    title={n}
                                >
                                    {n}
                                </li>
                            ))}
                        </ul>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Modal de edição do template */}
            <EsteiraBroadcastEditDialog
                open={editOpen}
                onOpenChange={setEditOpen}
                items={editDialogItems}
                pageNameById={pageNameById}
                groupId={groupId}
                horarioLabel={fmtDate(horario)}
                tplId={tplId}
            />
        </>
    );
}

// ─── HistoryCard: broadcast (qualquer status) com preview + métricas ─

function HistoryCard({
    broadcast, pageName,
}: {
    broadcast: any;
    pageName: string;
}) {
    const { title, subtitle, imageUrl, text, buttons } = extractPreview(broadcast);
    // Preview do template de fallback pré-selecionado (se o grupo tem fallback).
    const fbPreview = broadcast.fallback_message_payload
        ? extractPreview({ message_payload: broadcast.fallback_message_payload })
        : null;
    const status: EsteiraBroadcastStatus = broadcast.status;
    const statusLabel: Record<EsteiraBroadcastStatus, string> = {
        pending: 'Pendente',
        sending: 'Enviando',
        sent: 'Enviado',
        failed: 'Falhou',
        skipped: 'Pulado',
        cancelled: 'Cancelado',
    };
    const isErrorRetry = broadcast.lead_filter === 'error_retry';

    return (
        <div
            className={`border rounded-lg p-3 hover:border-violet-300 transition-colors flex gap-3 ${
                isErrorRetry
                    ? 'bg-amber-50/40 border-amber-200'
                    : 'bg-white'
            }`}
        >
            {/* Lateral esquerda: horário + página + status + leads */}
            <div className="flex flex-col items-start gap-1.5 min-w-[140px] shrink-0">
                <div className="text-xs font-mono font-semibold text-violet-700">
                    {fmtDate(broadcast.scheduled_at)}
                </div>
                <div className="text-[11px] text-gray-700 truncate max-w-[140px]" title={pageName}>
                    {pageName}
                </div>
                <Badge variant="outline" className={`text-[10px] ${BROADCAST_STATUS_COLORS[status]}`}>
                    {statusLabel[status] || status}
                </Badge>
                {isErrorRetry && (
                    <Badge
                        variant="outline"
                        className="text-[10px] bg-amber-100 text-amber-800 border-amber-300 font-medium"
                        title="Disparo extra depois do horário normal, mirando leads que tiveram erro recuperável (ex: 551)"
                    >
                        ↻ Retry erros
                    </Badge>
                )}
                {/* Leads enviados (só pra sent/failed) */}
                {(status === 'sent' || status === 'failed') && (
                    <div className="text-[10px] text-gray-600 pt-1">
                        <span className="text-emerald-700 font-semibold">{broadcast.total_sent}</span>
                        <span className="text-gray-400"> / {broadcast.total_leads}</span>
                        <span className="text-gray-500"> leads</span>
                        {broadcast.total_failed > 0 && (
                            <div className="text-red-500 text-[10px]">{broadcast.total_failed} falhas</div>
                        )}
                    </div>
                )}
                {broadcast.error_code != null && (
                    <div className="text-[10px] text-red-500 font-mono">
                        Erro #{broadcast.error_code}
                    </div>
                )}
                <Badge variant="outline" className="text-[9px]">
                    tpl #{broadcast.template_intel_id}
                </Badge>
            </div>

            {/* Preview Messenger-like — primário (raw) + fallback aprovado */}
            <div className="flex-1 min-w-0 space-y-2">
                <PreviewBubble
                    title={title}
                    subtitle={subtitle}
                    imageUrl={imageUrl}
                    text={text}
                    buttons={buttons}
                />
                {fbPreview && (
                    <div className="rounded-lg border border-dashed border-violet-200 bg-violet-50/30 p-2">
                        <div className="text-[10px] font-medium text-violet-700 mb-1 flex flex-wrap items-center gap-1">
                            🛟 Fallback aprovado
                            {broadcast.fallback_template_name && (
                                <span className="text-violet-400 font-normal">· {broadcast.fallback_template_name}</span>
                            )}
                            {broadcast.total_sent_fallback > 0 ? (
                                <span className="text-emerald-600 font-normal">
                                    ({broadcast.total_sent_fallback} enviados via fallback)
                                </span>
                            ) : (
                                <span className="text-gray-400 font-normal">(usado se o primário falhar fora da janela)</span>
                            )}
                        </div>
                        <PreviewBubble
                            title={fbPreview.title}
                            subtitle={fbPreview.subtitle}
                            imageUrl={fbPreview.imageUrl}
                            text={fbPreview.text}
                            buttons={fbPreview.buttons}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── PreviewBubble: shared between UpcomingCard + history popover ──

function PreviewBubble({
    title, subtitle, imageUrl, text, buttons,
}: {
    title?: string;
    subtitle?: string;
    imageUrl?: string;
    text?: string;
    buttons: Array<{ title?: string; url?: string }>;
}) {
    return (
        <div className="bg-gray-50 rounded-2xl rounded-tl-sm border border-gray-200 overflow-hidden max-w-[280px]">
            {imageUrl && (
                <div className="aspect-[1.91/1] bg-gray-100 overflow-hidden">
                    <img src={imageUrl} alt="" loading="lazy" className="w-full h-full object-cover" />
                </div>
            )}
            <div className="p-2.5 space-y-0.5">
                {title && (
                    <p className="text-[13px] font-semibold text-gray-900 line-clamp-2 leading-tight">
                        {title}
                    </p>
                )}
                {subtitle && (
                    <p className="text-[11px] text-gray-600 line-clamp-2 leading-tight">
                        {subtitle}
                    </p>
                )}
                {text && (
                    <p className="text-[12px] text-gray-900 line-clamp-3 leading-snug whitespace-pre-line">
                        {text}
                    </p>
                )}
            </div>
            {buttons.length > 0 && (
                <div className="border-t bg-white">
                    {buttons.slice(0, 3).map((b, i) => (
                        <div
                            key={i}
                            className={`px-3 py-1.5 text-center text-[11px] text-violet-600 font-medium truncate ${
                                i > 0 ? 'border-t border-gray-100' : ''
                            }`}
                        >
                            {b.title || '(sem texto)'}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function extractPreview(broadcast: any): {
    title?: string;
    subtitle?: string;
    imageUrl?: string;
    text?: string;
    buttons: Array<{ title?: string; url?: string }>;
} {
    const payload = broadcast.message_payload || {};
    const att = payload.attachment || {};
    const attPayload = att.payload || {};
    const isCard = attPayload.template_type === 'generic'
        || Array.isArray(attPayload.elements);

    let title: string | undefined;
    let subtitle: string | undefined;
    let imageUrl: string | undefined;
    let text: string | undefined;
    let buttons: Array<{ title?: string; url?: string }> = [];

    if (isCard && Array.isArray(attPayload.elements) && attPayload.elements[0]) {
        const el = attPayload.elements[0];
        title = el.title;
        subtitle = el.subtitle;
        imageUrl = el.image_url;
        buttons = el.buttons || [];
    } else if (attPayload.text) {
        text = attPayload.text;
        buttons = attPayload.buttons || [];
    }

    if (buttons.length === 0 && Array.isArray(broadcast.applied_buttons)) {
        buttons = broadcast.applied_buttons;
    }

    return { title, subtitle, imageUrl, text, buttons };
}
